import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { convertBoolDiffValue } from "./valueFunctions";
import RiskValueDriverRegulatoryForm from "./RiskValueDriverRegulatoryForm";
import { getIconFromDriverName } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const RiskValueDriverRegulatory = ({ id, valueModel, risks, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [open, setOpen] = React.useState(false);
    const [selectedRisk, setSelectedRisk] = useState(risks[0]['name'] || '')
    const [deleteRegulatoryDriverPopupOpen, setDeleteRegulatoryDriverPopupOpen] = useState(false);
    const [deleteRegulatoryDriverId, setDeleteRegulatoryDriverId] = useState(null);
    const [editRegulatoryDriverId, setEditRegulatoryDriverId] = useState(null);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleRiskChange = (event) => {
        setSelectedRisk(event.target.value)
        if (editRegulatoryDriverId) {
            setEditRegulatoryDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteRegulatoryDriver = (id) => {
        setDeleteRegulatoryDriverId(id)
        setDeleteRegulatoryDriverPopupOpen(true)
    }

    const editRegulatoryDriver = (id) => {
        setEditRegulatoryDriverId(id)
        let regulatoryDriver = valueModel.risk_value_driver.regulatory_reduction.find(regulatoryDriver => regulatoryDriver.id === id)

        setSelectedRisk(regulatoryDriver.type)
    }

    const onSubmitRegulatory = async (values, driver, risk, { setSubmitting, setErrors }) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.id = editRegulatoryDriverId;
        if (outcome) {
            clonedValues.outcome = outcome;
        }
        clonedValues.type = selectedRisk;
        clonedValues.fine_value = clonedValues.fine_value.toString().replace(/,/g, "");

        let success = false

        if (editRegulatoryDriverId) {
            const result = await dispatch(editDriver('regulatory', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditRegulatoryDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('regulatory', id, clonedValues, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetRegulatory = () => {
        setEditRegulatoryDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const regulatoryDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'sub_risk', label: 'Employee', format: (value) => formatString(value), minWidth: 100, action: false },
        // { id: 'multiple_type', label: 'Type', format: (value) => firstCharToUpper(value), minWidth: 100, action: false },
        { id: 'risk_reduction', label: 'Risk', minWidth: 100, action: false },
        // { id: 'occurrence', label: 'Occurrence', format: (value) => value + ' %', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createRegulatoryDriverData = (id, outcome, driver_name, risk_reduction, positive_driver, actions) => {
        risk_reduction = `${risk_reduction} % ${positive_driver ? 'reduction' : 'increase'}`
        return { id, outcome, driver_name, risk_reduction, positive_driver, actions };
    }

    const regulatoryDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editRegulatoryDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteRegulatoryDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const regulatoryDriverRows = valueModel.risk_value_driver.regulatory_reduction.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createRegulatoryDriverData(driver.id, driver.outcome, driver.driver_name, driver.risk_reduction, driver.positive_driver, regulatoryDriverActions(driver.id))
        )
    })

    const regulatoryDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={regulatoryDriverColumns}
                    rows={regulatoryDriverRows}
                    selectedId={editRegulatoryDriverId}
                />
            </React.Fragment>
        )
    }

    const regulatoryDriverFormActions = () => {
        return (
            <React.Fragment>
                {editRegulatoryDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editRegulatoryDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetRegulatory}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Regulatory Risk Reduction
                </Typography>
            </Stack>

            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 4 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedRisk}
                        onChange={handleRiskChange}
                        size='small'
                    >
                        {risks.map((risk, index) => (
                            <MenuItem key={index} value={risk['name']}>
                                {risk['name']}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <RiskValueDriverRegulatoryForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                selectedRisk={selectedRisk}
                riskStructure={risks.find(category => category.name === selectedRisk)}
                editRegulatoryDriverId={editRegulatoryDriverId}
                editRegulatoryDriver={valueModel.risk_value_driver.regulatory_reduction.find(regulatoryDriver => regulatoryDriver.id === editRegulatoryDriverId)}
                formActions={regulatoryDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitRegulatory(values, driver, selectedRisk, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {regulatoryDriverTable()}
            <DeletePopup open={deleteRegulatoryDriverPopupOpen} onClose={() => setDeleteRegulatoryDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('regulatory', id, deleteRegulatoryDriverId))} />
        </Box>
    )
}

export default RiskValueDriverRegulatory